import { Icon, Spinner } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import apis from 'browser/app/models/apis'
import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import { ViewRenderer } from 'shared-libs/components/view/renderer'
import {
  FIRE_DEFAULT_FIRE_BUTTON_VARIANT,
  FireButtonVariant,
} from 'shared-libs/components/ui2/fields/core-field/fire-field-props'
import {
  buildFireLazyScope,
  FireDebugPanelProps,
  FireInlineLookupInputProps,
  FireLoadingIndicatorProps,
  FireRetryButtonProps,
  FireTriggerButtonProps,
  FIRE_DEFAULT_LOOKUP_PLACEHOLDER,
  FIRE_LOOKUP_BUTTON_APPEAR_DELAY_MS,
  FIRE_SLOW_LOADING_MESSAGE,
  isFireLookupQueryValid,
} from 'shared-libs/components/ui2/fields/field-ui/fire-field-ui/fire-field-ui-props'

import './_fire-field-ui.scss'

/**
 * Presentational pieces shared by `ui2:apiField` and `ui2:eventField`. Both fields decide *when* to
 * show these and what the copy says; the markup and styling live here once.
 */

const buttonClasses = (variant: FireButtonVariant | undefined) => {
  const resolved = {
    variant: variant?.variant ?? FIRE_DEFAULT_FIRE_BUTTON_VARIANT.variant,
    color: variant?.color ?? FIRE_DEFAULT_FIRE_BUTTON_VARIANT.color,
  }
  // Namespaced separately: `primary` is a legal value of both axes, so a single `--primary`
  // modifier would make an outlined primary-colored button match the filled rule.
  return classNames(
    'ui2-fireField__button',
    `ui2-fireField__button--variant-${resolved.variant}`,
    `ui2-fireField__button--color-${resolved.color}`,
  )
}

export const UI2FireTriggerButton: React.FC<FireTriggerButtonProps> = ({
  label, variant, isDisabled, isLoading, onPress,
}) => (
  <div className='ui2-fireField__buttonWrap'>
    <button
      type='button'
      className={buttonClasses(variant)}
      disabled={isDisabled || isLoading}
      onClick={onPress}
    >
      {isLoading ? <Spinner size={14} /> : label}
    </button>
  </div>
)

export const UI2FireRetryButton: React.FC<FireRetryButtonProps> = ({
  label, variant, isLoading, isDisabled, onPress,
}) => (
  <div className='ui2-fireField__buttonWrap'>
    <button
      type='button'
      className={buttonClasses(variant)}
      onClick={onPress}
      disabled={isLoading || isDisabled}
    >
      {isLoading ? <Spinner size={14} /> : (label ?? 'Retry')}
    </button>
  </div>
)

/** Inline spinner + text. Never a banner — a request in flight is not an outcome. */
export const UI2FireLoadingIndicator: React.FC<FireLoadingIndicatorProps> = ({
  isSlow, message,
}) => {
  const text = isSlow ? FIRE_SLOW_LOADING_MESSAGE : message
  return (
    <div className='ui2-fireField__loading'>
      <Spinner size={14} />
      {text && <span className='ui2-fireField__loadingMessage'>{text}</span>}
    </div>
  )
}

interface IUI2FireInlineLookupState {
  query: string
  /** True once the current text has been submitted, which drops the pending treatment. */
  isCommitted: boolean
  isButtonVisible: boolean
}

/**
 * The `fireMode: 'inlineLookup'` control — a search input whose round submit button fires the
 * field. Deliberately mirrors `ui2:entitySearchField`'s submit mode (shared button styles, the same
 * appearance delay and pulse, the same italic treatment for uncommitted text) so the two read as
 * one affordance; it differs only in what submitting does.
 */
export class UI2FireInlineLookup extends React.Component<
  FireInlineLookupInputProps,
  IUI2FireInlineLookupState
> {
  private buttonShowTimer: ReturnType<typeof setTimeout> | null = null

  constructor(props: FireInlineLookupInputProps) {
    super(props)
    this.state = { query: '', isCommitted: false, isButtonVisible: false }
  }

  public componentWillUnmount() {
    this.clearButtonTimer()
  }

  private clearButtonTimer() {
    if (this.buttonShowTimer !== null) {
      clearTimeout(this.buttonShowTimer)
      this.buttonShowTimer = null
    }
  }

  private get canSubmit(): boolean {
    const { minQueryLength, isDisabled, isLoading, isSubmitDisabled } = this.props
    return isFireLookupQueryValid(this.state.query, minQueryLength)
      && !isDisabled && !isLoading && !isSubmitDisabled
  }

  private handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value
    this.setState({ query, isCommitted: false })
    this.props.onQueryChange?.(query)

    // Same delayed reveal entitySearchField uses: the button appears a beat after typing stops, so
    // it doesn't flicker in and out on every keystroke.
    this.clearButtonTimer()
    if (!isFireLookupQueryValid(query, this.props.minQueryLength)) {
      this.setState({ isButtonVisible: false })
      return
    }
    this.buttonShowTimer = setTimeout(() => {
      this.buttonShowTimer = null
      this.setState({ isButtonVisible: true })
    }, FIRE_LOOKUP_BUTTON_APPEAR_DELAY_MS)
  }

  private handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.handleSubmit()
    }
  }

  private handleSubmit = () => {
    if (!this.canSubmit) return
    this.setState({ isCommitted: true })
    this.props.onSubmit(_.trim(this.state.query))
  }

  public render() {
    const { placeholder, isDisabled, isLoading, isSubmitDisabled } = this.props
    const { query, isCommitted, isButtonVisible } = this.state
    const showPulse = isButtonVisible && this.canSubmit && !isCommitted

    return (
      <div className='ui2-fireInlineLookup'>
        <UI2FieldControl
          rightAddon={
            <span className='ui2-fireInlineLookup-buttonWrap'>
              <button
                type='button'
                className={classNames('ui2-fireInlineLookup-button', {
                  'ui2-fireInlineLookup-button--pulse': showPulse,
                  'ui2-fireInlineLookup-button--muted': !this.canSubmit,
                })}
                onClick={this.handleSubmit}
                disabled={!this.canSubmit}
                aria-label='Submit lookup'
              >
                {isLoading
                  ? <Spinner size={14} />
                  : <Icon icon={IconNames.ARROW_RIGHT} />}
              </button>
            </span>
          }
        >
          <input
            className={classNames('ui2-fireInlineLookup-input', {
              'ui2-fireInlineLookup-input--pending': !_.isEmpty(query) && !isCommitted,
            })}
            type='search'
            placeholder={placeholder ?? FIRE_DEFAULT_LOOKUP_PLACEHOLDER}
            value={query}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            disabled={isDisabled || isLoading || isSubmitDisabled}
          />
        </UI2FieldControl>
      </div>
    )
  }
}

/**
 * Renders a `lazySuccessUiSchema` / `lazyFailureUiSchema` snippet with the field's response in
 * scope. Read-only by construction — this is the `ViewRenderer` path, the same one behind
 * `ui2:selectField` option templates, so display components work and bound fields do not write
 * back to the form.
 */
export const UI2FireLazyContent: React.FC<{ uiSchema: any, scope: any, frames?: any }> = ({
  uiSchema, scope, frames,
}) => {
  if (!uiSchema) return null
  const ComponentsMap = require('browser/components').default
  return (
    <div className='ui2-fireField__lazyContent'>
      <ViewRenderer
        schema={uiSchema}
        state={{ components: ComponentsMap, frames, ...buildFireLazyScope(frames, scope) }}
        componentsMap={ComponentsMap}
        api={apis}
      />
    </div>
  )
}

export const UI2FireDebugPanel: React.FC<FireDebugPanelProps> = ({
  title, isLoading, rows, blocks,
}) => (
  <div className='ui2-fireField__debug'>
    <div className='ui2-fireField__debugHeader'>
      <span>{title}</span>
      {isLoading && <Spinner size={14} className='ui2-fireField__debugSpinner' />}
    </div>
    {rows.map((row) => (
      <div key={row.label}>{row.label}: <code>{row.value}</code></div>
    ))}
    {(blocks ?? []).map((block) => (
      <React.Fragment key={block.label}>
        <div>{block.label}:</div>
        <pre>{block.content}</pre>
      </React.Fragment>
    ))}
  </div>
)
